import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import Select from "react-select";

/**
 * Cells for AdminTable. Each reads saveRow from the table instance, so the
 * page's columns never need to change when a row does.
 */

export interface ITag {
	id: number | null;
	name: string;
}

/**
 * Saves on Enter or when it loses focus, and only when the text changed.
 * Escape puts the old text back.
 */
export const EditableTextCell = ({ value, row, column, saveRow }: any) => {
	const original = value == null ? "" : String(value);
	const [text, setText] = useState(original);
	const cancelled = useRef(false);

	useEffect(() => setText(original), [original]);

	const commit = () => {
		const next = text.trim();

		if (
			cancelled.current ||
			next === original ||
			(column.required && !next)
		) {
			cancelled.current = false;
			setText(original);
			return;
		}

		saveRow(row.original.id, { [column.id]: next });
	};

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.currentTarget.blur();
		} else if (event.key === "Escape") {
			cancelled.current = true;
			event.currentTarget.blur();
		}
	};

	return (
		<Form.Control
			size="sm"
			value={text}
			aria-label={column.Header}
			onChange={event => setText(event.target.value)}
			onBlur={commit}
			onKeyDown={onKeyDown}
		/>
	);
};

/**
 * A switch that saves as soon as it's flipped.
 */
export const SwitchCell = ({ value, row, column, saveRow }: any) => (
	<Form.Check
		type="switch"
		id={`${column.id}-${row.original.id}`}
		label={value ? column.onLabel : column.offLabel}
		checked={!!value}
		onChange={event =>
			saveRow(row.original.id, { [column.id]: event.target.checked })
		}
	/>
);

/**
 * Shows the linked bundles or libraries as tags. Clicking opens a picker,
 * which saves when it closes if the selection changed.
 */
export const TagsCell = ({ value, row, column, saveRow }: any) => {
	const [editing, setEditing] = useState(false);
	const tags: ITag[] = value || [];

	if (!editing) {
		return (
			<button
				type="button"
				className="admin-tags"
				onClick={() => setEditing(true)}
				title={`Edit ${column.Header.toLowerCase()}`}
			>
				{tags.length === 0 && (
					<span className="admin-tags-empty">None</span>
				)}
				{tags.map(tag => (
					<span key={tag.id as number} className="admin-tag">
						{tag.name}
					</span>
				))}
				<i className="fa fa-pencil admin-tags-edit" />
			</button>
		);
	}

	return (
		<TagsPicker
			options={column.options}
			selected={tags}
			onDone={(next: ITag[]) => {
				setEditing(false);
				if (!sameIds(next, tags)) {
					saveRow(row.original.id, { [column.id]: next });
				}
			}}
		/>
	);
};

const TagsPicker = ({ options, selected, onDone }: any) => {
	const [picked, setPicked] = useState<ITag[]>(selected);
	const finished = useRef(false);
	const toOption = (tag: ITag) => ({ value: tag.id, label: tag.name, tag });

	// Done, Escape and losing focus can all arrive for one close
	const finish = (tags: ITag[]) => {
		if (!finished.current) {
			finished.current = true;
			onDone(tags);
		}
	};

	return (
		<div className="admin-tags-picker">
			<Select
				autoFocus={true}
				defaultMenuIsOpen={true}
				isMulti={true}
				isClearable={false}
				closeMenuOnSelect={false}
				options={options.map(toOption)}
				value={picked.map(toOption)}
				onChange={(chosen: any) =>
					setPicked((chosen || []).map((option: any) => option.tag))
				}
				onBlur={() => finish(picked)}
				onKeyDown={(event: any) => {
					if (event.key === "Escape") {
						finish(selected);
					}
				}}
				menuPortalTarget={document.body}
				styles={{
					menuPortal: (base: any) => ({ ...base, zIndex: 1060 }),
					menu: (base: any) => ({ ...base, fontSize: "0.85rem" }),
				}}
			/>
			<Button size="sm" variant="link" onMouseDown={() => finish(picked)}>
				Done
			</Button>
		</div>
	);
};

function sameIds(a: ITag[], b: ITag[]): boolean {
	const ids = (tags: ITag[]) =>
		tags
			.map(tag => tag.id)
			.sort()
			.join(",");
	return ids(a) === ids(b);
}

/**
 * Row status: a spinner while saving, a tick once saved, a warning with the
 * reason if it failed.
 */
export const StatusCell = ({ row, statuses }: any) => {
	const status = statuses[row.original.id];

	if (!status) {
		return null;
	}

	if (status.state === "saving") {
		return (
			<i className="fa fa-spinner fa-spin text-muted" title="Saving" />
		);
	}

	if (status.state === "saved") {
		return <i className="fa fa-check text-success" title="Saved" />;
	}

	return (
		<i
			className="fa fa-exclamation-triangle text-danger"
			title={status.message}
		/>
	);
};

// Filters ------------------------------------------------

/**
 * Text search for a column. Clicks don't reach the header, so typing in it
 * doesn't also sort the column.
 */
export const TextFilter = ({
	column: { filterValue, setFilter, Header },
}: any) => (
	<Form.Control
		size="sm"
		className="mt-1"
		value={filterValue || ""}
		placeholder="Search..."
		aria-label={`Search ${Header}`}
		onClick={event => event.stopPropagation()}
		onChange={event => setFilter(event.target.value || undefined)}
	/>
);

/**
 * All / on / off for a switch column.
 */
export const SwitchFilter = ({
	column: { filterValue, setFilter, Header, onLabel, offLabel },
}: any) => (
	<Form.Select
		size="sm"
		className="mt-1"
		value={filterValue || ""}
		aria-label={`Filter ${Header}`}
		onClick={event => event.stopPropagation()}
		onChange={event => setFilter(event.target.value || undefined)}
	>
		<option value="">All</option>
		<option value="on">{onLabel}</option>
		<option value="off">{offLabel}</option>
	</Form.Select>
);

export function switchFilter(rows: any[], ids: string[], filterValue: string) {
	return rows.filter(row => !!row.values[ids[0]] === (filterValue === "on"));
}

export function tagsFilter(rows: any[], ids: string[], filterValue: string) {
	const search = filterValue.toLowerCase();
	return rows.filter(row =>
		(row.values[ids[0]] || []).some((tag: ITag) =>
			tag.name.toLowerCase().includes(search),
		),
	);
}
